'use client';

import { MessageBroker } from '@/utils/message-broker';
import { MESSAGES, type Message } from '@/utils/message-broker/types';
import { isMessageFromEmbeddedIframe } from '@/utils/message-broker/embedded-iframe-message';
import { useEffect } from 'react';
import { useAccountUm } from '../use-account-um';
import { useStore } from '@/store/store';
import { useGlobalContext } from '@/store/contexts/global-context';

export const useMessageListener = () => {
    const { isAndroidWrapper } = useGlobalContext();
    const { logOutWithModal } = useAccountUm();

    const handleReloadRequired = (msg: Message) => {
        // Read isLoggedIn from the live store, not the SSR-captured GlobalContext:
        // when a logged-out user completes the deposit-CTA flow via the
        // state-selector, useGlobalContext().isLoggedIn would still be the
        // stale SSR `false` and we'd incorrectly fire location.reload() on a
        // user who is actually logged in.
        const isLoggedIn = useStore.getState().userContext.isLoggedIn;
        const embeddedGame = useStore.getState().embeddedGame;

        if (isMessageFromEmbeddedIframe(msg)) {
            embeddedGame.iframeRef?.contentWindow?.location.reload();
            return;
        }

        if (!isLoggedIn && msg.origin.includes('launcher')) {
            return location.reload();
        }

        if (!isLoggedIn && isAndroidWrapper) {
            location.reload();
        }
    };

    const handleLogoutRequired = () => {
        logOutWithModal();
        useStore.getState().embeddedGame.close();
    };

    const handleAccountHubShowBalanceChanged = (msg: Message) => {
        if (!isAndroidWrapper) return;
        const data = msg.data as { type: string; showBalance?: boolean };
        useStore.getState().userContext.accountSettings.setShowBalance(!!data.showBalance);
    };

    const handleMessage = (message: Message) => {
        const MESSAGES_MAPPING = {
            [MESSAGES.LOGOUT_REQUIRED]: handleLogoutRequired,
            [MESSAGES.RELOAD_REQUIRED]: handleReloadRequired,
            [MESSAGES.ACCOUNT_HUB_SHOW_BALANCE_CHANGED]: handleAccountHubShowBalanceChanged
        };

        const fn = MESSAGES_MAPPING[message.data.type];
        if (fn) fn(message);
    };

    useEffect(() => {
        const cleanup = MessageBroker.register();

        MessageBroker.subscribe(handleMessage);

        return () => {
            MessageBroker.unsubscribe(handleMessage);
            cleanup();
        };
    }, []);
};
